import React, { useState, useEffect } from 'react';
import { notify, confirmDialog } from './ui/notify';
import { apiCall } from './config';

const BackupManager = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Control Panel State
  const [activeTab, setActiveTab] = useState('backups');
  const [systemStatus, setSystemStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [dbStatus, setDbStatus] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [logLevel, setLogLevel] = useState('all');

  const loadBackups = async () => {
    try {
      const data = await apiCall('/backups');
      setBackups(data.backups || []);
      setLastUpdate(new Date().toLocaleString('de-DE'));
    } catch (error) {
      console.error('Fehler beim Laden der Backups:', error);
    } finally {
      setLoading(false);
    }
  };

  const createManualBackup = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/backups/create', { method: 'POST' });
      
      if (data.success) {
        notify('✅ Backup erfolgreich erstellt!');
        loadBackups();
      } else {
        notify('ℹ️ ' + data.message);
      }
    } catch (error) {
      console.error('Fehler beim Erstellen des Backups:', error);
      notify('❌ Fehler beim Erstellen des Backups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBackups();
    // Alle 5 Minuten aktualisieren
    const interval = setInterval(loadBackups, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatFileSize = (kb) => {
    if (kb < 1024) return `${kb} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  // Control Panel Functions
  const loadSystemStatus = async () => {
    try {
      const response = await apiCall('/system-status');
      setSystemStatus(response);
    } catch (error) {
      console.error('Error loading system status:', error);
    }
  };

  const loadLogs = async () => {
    try {
      const level = logLevel === 'all' ? '' : logLevel;
      const response = await apiCall(`/system-logs?limit=100&level=${level}`);
      setLogs(response);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  const loadDbStatus = async () => {
    try {
      const response = await apiCall('/database-status');
      setDbStatus(response);
    } catch (error) {
      console.error('Error loading database status:', error);
    }
  };

  const loadHealthStatus = async () => {
    try {
      const response = await apiCall('/health-check');
      setHealthStatus(response);
    } catch (error) {
      console.error('Error loading health status:', error);
    }
  };

  const restartBackend = async () => {
    if (!await confirmDialog('Backend wirklich neustarten? Dies kann zu einer kurzen Unterbrechung führen.')) {
      return;
    }
    
    try {
      await apiCall('/restart-backend', { method: 'POST' });
      notify('Backend wird neugestartet...');
      // Wait a bit then try to reconnect
      setTimeout(() => {
        loadHealthStatus();
      }, 5000);
    } catch (error) {
      notify('Fehler beim Neustarten: ' + error.message);
    }
  };

  const refreshAllData = () => {
    if (activeTab === 'control') {
      loadSystemStatus();
      loadLogs();
      loadDbStatus();
      loadHealthStatus();
    }
  };

  // Auto-refresh effect
  useEffect(() => {
    let interval;
    if (autoRefresh && activeTab === 'control') {
      interval = setInterval(refreshAllData, 5000); // Refresh every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, activeTab]);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'control') {
      refreshAllData();
    } else if (activeTab === 'backups') {
      loadBackups();
    }
  }, [activeTab, logLevel]);

  const getBackupTypeColor = (type) => {
    switch (type) {
      case 'hourly': return '#4CAF50'; // Grün
      case 'manual': return '#2196F3'; // Blau
      default: return '#757575'; // Grau
    }
  };

  const getBackupTypeLabel = (type) => {
    switch (type) {
      case 'hourly': return 'Stündlich';
      case 'manual': return 'Manuell';
      default: return type;
    }
  };

  if (loading && backups.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Lade Backup-Informationen...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        borderBottom: '2px solid #eee',
        paddingBottom: '15px'
      }}>
        <h2 style={{ margin: 0, color: '#333' }}>
          🎛️ System-Verwaltung
        </h2>
      </div>

      {/* Tab Navigation */}
      <div style={{ 
        marginBottom: '20px',
        borderBottom: '1px solid #eee'
      }}>
        <div style={{ display: 'flex', gap: '0' }}>
          <button
            onClick={() => setActiveTab('backups')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'backups' ? '#2196F3' : 'transparent',
              color: activeTab === 'backups' ? 'white' : '#666',
              border: 'none',
              borderBottom: activeTab === 'backups' ? '3px solid #1976D2' : '3px solid transparent',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            �️ Backups
          </button>
          <button
            onClick={() => setActiveTab('control')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'control' ? '#2196F3' : 'transparent',
              color: activeTab === 'control' ? 'white' : '#666',
              border: 'none',
              borderBottom: activeTab === 'control' ? '3px solid #1976D2' : '3px solid transparent',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            🎛️ Control Panel
          </button>
        </div>
      </div>

      {/* Backup Tab */}
      {activeTab === 'backups' && (
        <>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
            <button
              onClick={loadBackups}
              disabled={loading}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              🔄 Aktualisieren
            </button>
            <button
              onClick={createManualBackup}
              disabled={loading}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              💾 Manuelles Backup
            </button>
            {lastUpdate && (
              <div style={{ 
                fontSize: '14px', 
                color: '#666',
                marginLeft: 'auto'
              }}>
                Letzte Aktualisierung: {lastUpdate}
              </div>
            )}
          </div>
        </>
      )}

      {/* Control Panel Tab */}
      {activeTab === 'control' && (
        <>
          {/* Control Buttons */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={refreshAllData}
              style={{
                padding: '10px 20px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔄 Aktualisieren
            </button>
            <button
              onClick={restartBackend}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔄 Backend Neustarten
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-Refresh (5s)
            </label>
          </div>

          {/* System Status Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
            {/* System Status */}
            <div style={{ backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>💻 System Status</h3>
              {systemStatus ? (
                <>
                  <p><strong>Platform:</strong> {systemStatus.system.platform} ({systemStatus.system.arch})</p>
                  <p><strong>Hostname:</strong> {systemStatus.system.hostname}</p>
                  <p><strong>Uptime:</strong> {Math.floor(systemStatus.system.uptime / 3600)}h {Math.floor((systemStatus.system.uptime % 3600) / 60)}m</p>
                  
                  <h4>🧠 Memory</h4>
                  <div style={{ 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '10px', 
                    height: '20px', 
                    marginBottom: '5px',
                    position: 'relative'
                  }}>
                    <div style={{ 
                      backgroundColor: parseFloat(systemStatus.memory.usagePercent) > 80 ? '#f44336' : 
                                     parseFloat(systemStatus.memory.usagePercent) > 60 ? '#ff9800' : '#4CAF50',
                      height: '100%',
                      width: `${systemStatus.memory.usagePercent}%`,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {systemStatus.memory.usagePercent}%
                    </div>
                  </div>
                  <small>{systemStatus.memory.used} / {systemStatus.memory.total}</small>
                  
                  <h4 style={{ marginTop: '15px' }}>🖥️ CPU</h4>
                  <p><strong>Model:</strong> {systemStatus.cpu.model}</p>
                  <p><strong>Cores:</strong> {systemStatus.cpu.cores}</p>
                  <p><strong>Load:</strong> {systemStatus.cpu.loadAverage.join(', ')}</p>
                </>
              ) : (
                <p>Lade System-Status...</p>
              )}
            </div>

            {/* Disk Usage */}
            <div style={{ backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>💿 Festplatten</h3>
              {systemStatus && systemStatus.disk ? (
                systemStatus.disk.map((disk, index) => (
                  <div key={index} style={{ marginBottom: '15px' }}>
                    <h4 style={{ margin: '0 0 5px 0' }}>{disk.drive}</h4>
                    <div style={{ 
                      backgroundColor: '#f0f0f0', 
                      borderRadius: '10px', 
                      height: '20px', 
                      marginBottom: '5px',
                      position: 'relative'
                    }}>
                      <div style={{ 
                        backgroundColor: parseFloat(disk.usagePercent) > 90 ? '#f44336' : 
                                       parseFloat(disk.usagePercent) > 70 ? '#ff9800' : '#4CAF50',
                        height: '100%',
                        width: `${disk.usagePercent}%`,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {disk.usagePercent}%
                      </div>
                    </div>
                    <small>{disk.used} / {disk.total} (Frei: {disk.free})</small>
                  </div>
                ))
              ) : (
                <p>Lade Festplatten-Info...</p>
              )}
            </div>

            {/* Database Status */}
            <div style={{ backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>🗄️ Datenbank Status</h3>
              {dbStatus ? (
                <>
                  <p><strong>Status:</strong> 
                    <span style={{ 
                      marginLeft: '10px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      color: 'white',
                      backgroundColor: dbStatus.status === 'connected' ? '#4CAF50' : '#f44336'
                    }}>
                      {dbStatus.status}
                    </span>
                  </p>
                  <p><strong>Größe:</strong> {dbStatus.size}</p>
                  <h4>Tabellen-Statistiken:</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                      <h5 style={{ margin: '0 0 10px 0', color: '#666' }}>Main Database:</h5>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        <li>👥 Mitarbeiter: {dbStatus.mainDb?.users || 0}</li>
                        <li>🚉 Stationen: {dbStatus.mainDb?.stations || 0}</li>
                        <li>📅 Planungszuweisungen: {dbStatus.mainDb?.planning_assignments || 0}</li>
                        <li>⏱️ Ist-Stunden: {dbStatus.mainDb?.actual_hours || 0}</li>
                        <li>📄 Dokumente: {dbStatus.mainDb?.documents || 0}</li>
                        <li>♿ Rollstuhl-Buchungen: {dbStatus.mainDb?.wheelchair_bookings || 0}</li>
                        <li>🗓️ Verfügbarkeiten: {dbStatus.mainDb?.user_availability || 0}</li>
                        <li>📋 Arbeitsanweisungen: {dbStatus.mainDb?.work_instructions || 0}</li>
                      </ul>
                    </div>
                    <div>
                      <h5 style={{ margin: '0 0 10px 0', color: '#666' }}>Features Database:</h5>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        <li>📦 Gepäck-Anfragen: {dbStatus.featuresDb?.luggage_requests || 0}</li>
                        <li>📐 Station-Layouts: {dbStatus.featuresDb?.station_layouts || 0}</li>
                        <li>🚧 Zaun-Systeme: {dbStatus.featuresDb?.fence_systems || 0}</li>
                        <li>🎨 Custom Elements: {dbStatus.featuresDb?.custom_elements || 0}</li>
                      </ul>
                    </div>
                  </div>
                </>
              ) : (
                <p>Lade Datenbank-Status...</p>
              )}
            </div>

            {/* API Health */}
            <div style={{ backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>🔧 API Health Check</h3>
              {healthStatus ? (
                <>
                  <p><strong>Status:</strong> 
                    <span style={{ 
                      marginLeft: '10px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      color: 'white',
                      backgroundColor: healthStatus.status === 'healthy' ? '#4CAF50' : '#f44336'
                    }}>
                      {healthStatus.status}
                    </span>
                  </p>
                  <p><strong>Uptime:</strong> {Math.floor(healthStatus.uptime / 3600)}h {Math.floor((healthStatus.uptime % 3600) / 60)}m</p>
                  <p><strong>Node Version:</strong> {healthStatus.version}</p>
                  <p><strong>Environment:</strong> {healthStatus.environment}</p>
                  <h4>Prozess Memory:</h4>
                  <ul style={{ listStyle: 'none', padding: 0, fontSize: '14px' }}>
                    <li>RSS: {(healthStatus.memory.rss / (1024*1024)).toFixed(2)} MB</li>
                    <li>Heap Used: {(healthStatus.memory.heapUsed / (1024*1024)).toFixed(2)} MB</li>
                    <li>Heap Total: {(healthStatus.memory.heapTotal / (1024*1024)).toFixed(2)} MB</li>
                  </ul>
                </>
              ) : (
                <p>Lade API-Status...</p>
              )}
            </div>
          </div>

          {/* Logs Section */}
          <div style={{ backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#333' }}>📋 System Logs</h3>
              <select 
                value={logLevel} 
                onChange={(e) => setLogLevel(e.target.value)}
                style={{
                  padding: '5px 10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="all">Alle Logs</option>
                <option value="info">Info</option>
                <option value="error">Errors</option>
              </select>
            </div>
            <div style={{
              maxHeight: '400px',
              overflowY: 'auto',
              backgroundColor: '#f9f9f9',
              padding: '10px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '13px'
            }}>
              {logs.length > 0 ? (
                logs.map((log, index) => (
                  <div 
                    key={index} 
                    style={{
                      padding: '8px',
                      marginBottom: '5px',
                      borderRadius: '4px',
                      backgroundColor: log.level === 'error' ? '#ffebee' : '#fff'
                    }}
                  >
                    <span style={{ color: '#666' }}>[{new Date(log.timestamp).toLocaleString()}]</span>
                    <span style={{ 
                      marginLeft: '10px',
                      padding: '1px 6px',
                      borderRadius: '8px',
                      fontSize: '11px',
                      color: 'white',
                      backgroundColor: log.level === 'error' ? '#f44336' : '#2196F3'
                    }}>
                      {log.level.toUpperCase()}
                    </span>
                    <div style={{ marginTop: '3px' }}>{log.message}</div>
                  </div>
                ))
              ) : (
                <p>Keine Logs verfügbar</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Backup Tab Content - only show in backups tab */}
      {activeTab === 'backups' && (
        <>
          <div style={{
            backgroundColor: '#f9f9f9',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>📊 Backup-Übersicht</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <strong>Gesamte Backups:</strong> {backups.length}
          </div>
          <div>
            <strong>Stündliche Backups:</strong> {backups.filter(b => b.type === 'hourly').length}
          </div>
          <div>
            <strong>Manuelle Backups:</strong> {backups.filter(b => b.type === 'manual').length}
          </div>
          <div>
            <strong>Neuestes Backup:</strong> {backups[0]?.readable_time || 'Kein Backup'}
          </div>
        </div>
      </div>

      <div style={{
        backgroundColor: '#e3f2fd',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #2196F3'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#1976D2' }}>💡 Backup-System Info</h4>
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          <li><strong>Automatisch:</strong> Jede Stunde (nur bei Änderungen)</li>
          <li><strong>Aufbewahrung:</strong> 48 stündliche + 10 manuelle Backups</li>
          <li><strong>Format:</strong> Datum_Zeit für einfache Identifikation</li>
          <li><strong>Beispiel:</strong> "personalplanung_hourly_02092025_1430.db" = 2. Sept 2025, 14:30 Uhr</li>
        </ul>
      </div>

      {backups.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          backgroundColor: '#fff3cd',
          borderRadius: '8px',
          border: '1px solid #ffc107'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>📭</div>
          <h3>Keine Backups gefunden</h3>
          <p>Das Backup-System wird automatisch starten und stündlich Backups erstellen.</p>
        </div>
      ) : (
        <div style={{ 
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #ddd',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 120px 100px 150px 100px',
            gap: '10px',
            padding: '15px',
            backgroundColor: '#f5f5f5',
            fontWeight: 'bold',
            borderBottom: '1px solid #ddd'
          }}>
            <div>📅 Datum & Zeit</div>
            <div>🏷️ Typ</div>
            <div>📦 Größe</div>
            <div>📝 Dateiname</div>
            <div>🕐 DB-Stand</div>
          </div>
          
          {backups.map((backup, index) => (
            <div 
              key={backup.filename}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 120px 100px 150px 100px',
                gap: '10px',
                padding: '12px 15px',
                borderBottom: index < backups.length - 1 ? '1px solid #eee' : 'none',
                backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
              }}
            >
              <div style={{ fontWeight: 'bold' }}>
                {backup.readable_time}
              </div>
              <div>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  color: 'white',
                  backgroundColor: getBackupTypeColor(backup.type)
                }}>
                  {getBackupTypeLabel(backup.type)}
                </span>
              </div>
              <div>{formatFileSize(backup.file_size_kb)}</div>
              <div style={{ 
                fontSize: '12px', 
                fontFamily: 'monospace',
                wordBreak: 'break-all'
              }}>
                {backup.filename}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {new Date(backup.db_modified).toLocaleTimeString('de-DE', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            </div>
          ))}
        </div>
      )}
          </>
        )}
    </div>
  );
};

export default BackupManager;
